"use client";

import type { Solution } from "@/types/solution";
import dynamic from "next/dynamic";
import { Suspense } from "react";

const SolutionHero = dynamic(() => import("@/components/solutions/solution-hero").then(m => ({ default: m.SolutionHero })), { ssr: false });

const ProblemSection = dynamic(() => import("@/components/solutions/problem-section").then(m => ({ default: m.ProblemSection })), { ssr: false });
const FeaturesSection = dynamic(() => import("@/components/solutions/features-section").then(m => ({ default: m.FeaturesSection })), { ssr: false });
const IndustriesSection = dynamic(() => import("@/components/solutions/industries-section").then(m => ({ default: m.IndustriesSection })), { ssr: false });
const WhyChooseSection = dynamic(() => import("@/components/solutions/why-choose-section").then(m => ({ default: m.WhyChooseSection })), { ssr: false });
const SolutionWhyChoose = dynamic(() => import("@/components/solutions/solution-why-choose").then(m => ({ default: m.SolutionWhyChoose })), { ssr: false });
const FAQSection = dynamic(() => import("@/components/home/FAQSection").then(m => ({ default: m.FAQSection })), { ssr: false });
const ContactCommandCenter = dynamic(() => import("@/components/home/ContactCommandCenter").then(m => ({ default: m.default })), { ssr: false });
const CTASection = dynamic(() => import("@/components/home/CTASection").then(m => ({ default: m.CTASection })), { ssr: false });

function SectionSkeleton() {
  return <div className="min-h-[120px] animate-pulse bg-slate-100/60 rounded-2xl my-8" aria-hidden="true" />;
}

interface SolutionPageContentProps {
  solution: Solution;
}

export function SolutionPageContent({ solution }: SolutionPageContentProps) {
  return (
    <main className="min-h-screen">
      <SolutionHero hero={solution.hero} cta={solution.cta} slug={solution.slug} />
      <Suspense fallback={<SectionSkeleton />}>
        <ProblemSection problem={solution.problem} />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <FeaturesSection features={solution.features} />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <IndustriesSection
          industries={solution.industries}
          solutionName={(solution.hero.title || "").replace(/ERP|SOFTWARE/gi, "").trim()}
        />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <WhyChooseSection solution={solution} />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <SolutionWhyChoose />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <FAQSection items={solution.faqs} showContact={false} />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <ContactCommandCenter id="contact-form" />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <CTASection
          title={solution.cta.title}
          description={solution.cta.description}
          primaryLabel={solution.cta.primaryButton}
          secondaryLabel={solution.cta.secondaryButton}
          primaryHref="/contact"
          secondaryHref="#contact-form"
          onSecondaryClick={() => {
            const el = document.getElementById("contact-form");
            el?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        />
      </Suspense>
    </main>
  );
}
